/** Every string the learning editor shows, in one place. */
import { REFUSAL, RESULT } from "./editorModel";
import { FIX_KIND, HINT } from "./editorRules";

// The chat never ran a diagnosis: a pattern attaches to one. An open (or stuck) diagnosis takes a pattern without ending it.
const NO_DIAGNOSIS = "No diagnosis in this chat to attach the pattern to.";
const NOT_RECORDED = "The result of this diagnosis was not saved, so a pattern cannot be attached to it. Run the diagnosis again to teach it.";
const RECORDING = "Saving the diagnosis result… this takes a few seconds.";
const RECORDING_SLOW = "Saving the diagnosis result is taking longer than usual. Save becomes available as soon as it is done; Review checks again.";

export const TEXT = Object.freeze({
  title: "Teach Arta",
  close: "Close",
  back: "Back",
  preview: "Review",
  save: "Save as new pattern",
  saving: "Saving…",
  saveFailed: "Could not save. Try again.",
  done: "Done",
  stage: "Stage",
  progress: "Progress",
  notConnected: "Saving is not connected yet.",
  steps: ["Teach", "Review", "Done"],
  form: {
    heading: "Teach what you found",
    hint: "Check what Arta pre-filled, correct it, and say what the root cause was and what fixed it. Arta writes the pattern, its questions and its checks from this.",
    category: "Category",
    noCategory: "No category detected",
    symptoms: "Symptoms",
    symptomsHint: "What was seen, one per row. For example: Users at one office report slow browsing with the client connector on.",
    steps: "Troubleshooting steps",
    stepsHint: "What was tried, in order, one per row.",
    rootCause: "Root cause",
    rootCauseHint: "What was actually wrong",
    fix: "Fix or mitigation",
    fixHint: "What was done about the root cause, and how",
    truncated: (n) => `${n} more ${n === 1 ? "line was" : "lines were"} left out.`,
    item: "Item",
    add: "Add a row",
    remove: "Remove row",
  },
  // What is still needed, named exactly.
  requirements: Object.freeze({
    [HINT.CATEGORY]: "No category detected. Set it in the chat first.",
    [HINT.SYMPTOMS]: "Add at least one symptom.",
    [HINT.STEPS]: "Add at least one troubleshooting step.",
    [HINT.ROOT]: "Describe the root cause.",
    [HINT.FIX]: "Choose fix, mitigation or workaround and describe it.",
  }),
  // The name of each kind of fix, on the form and in the preview.
  fixKinds: Object.freeze({
    [FIX_KIND.FIX]: "Fix",
    [FIX_KIND.MITIGATION]: "Mitigation",
    [FIX_KIND.WORKAROUND]: "Workaround",
  }),
  stillNeeded: "Still needed",
  ready: "Everything needed is filled in. Review shows what would be learned.",
  previewScreen: {
    heading: "What would be learned",
    hint: "This is what Arta would record. Go back to change anything.",
    note: "Arta writes the pattern, its questions and its checks from these. The line of diagnostic is inferred.",
    category: "Category",
    symptoms: "Symptoms",
    steps: "Troubleshooting steps",
    rootCause: "Root cause",
    fix: "Fix or mitigation",
    dropped: "Left out",
  },
  // Where a prefilled line came from: a short tag on the row, with the longer explanation as its tooltip.
  provenance: Object.freeze({
    fact: { label: "Fact", hint: "A fact gathered in this chat" },
    note: { label: "Note", hint: "Noted from the engineer's own words" },
    unsure: { label: "Unsure", hint: "Stated with some doubt" },
    from: Object.freeze({
      handoff: "from the handoff", reader: "from the user's message", answer: "from an answer", seed: "from the start of the chat", engine: "run by the diagnosis",
    }),
  }),
  // Matching each line against what Arta already knows (the Review screen).
  match: Object.freeze({
    // the verdict banner, by match status
    matchingTitle: "Matching with what Arta already knows…",
    matchingHint: "Each line is compared with the catalog of this category. Any match can be changed before saving.",
    failedTitle: "Matching is not available right now",
    failedBody: "Arta may still be loading what it knows, which can take a few seconds the first time. Retry to match again, or save as is: every line is then saved as new.",
    existingPattern: (label) => `Matches an existing pattern: ${label}`,
    newPattern: "New pattern",
    newPatternHint: "Nothing similar in this category yet. The pattern will be added as new.",
    retry: "Retry",
    // one row: its state (on the control), what the control does, and the line under the text
    state: Object.freeze({ reused: "Reused", check: "Check match", new: "New", computing: "Matching…" }),
    change: "Change",
    changeFor: (text) => `Change match for: ${text}`,
    reuses: (label) => `Reuses: ${label}`,
    possible: (label) => `Possible match: ${label}`,
    confirmHint: "Confirm it or pick another.",
    willBeNew: "Will be added as new.",
    noCandidates: "Nothing similar found. Will be added as new.",
    noMatchData: "Not matched. Will be added as new.",
    failedRow: "Could not be matched. Will be added as new.",
    kindFact: "Fact",
    kindSymptom: "Symptom",
    reusesFact: (label) => `Reuses fact: ${label}`,
    kindOf: (kind) => `matched ${kind}`,
    fromRunbook: "from runbook",
    learned: "learned",
    strong: "strong match",
    weak: "possible match",
    // the candidate list
    yourLine: "Your line",
    chooseFor: (text) => `Match for: ${text}`,
    createNew: "Create new",
    createNewHint: "Adds this line to the catalog as written",
    causeLine: "Root cause",
    fixLine: "Fix",
    // the footer
    blocking: "Fix the flagged lines before saving.",
    failedHint: "Lines could not be matched. They are saved as new.",
    savePattern: "Save pattern",
    summary: ({ reused, check, new: fresh, pending }) => [
      `${reused} reused`, check ? `${check} to confirm` : null, `${fresh} new`, pending ? `${pending} pending` : null,
    ].filter(Boolean).join(" · "),
  }),
  result: Object.freeze({
    [RESULT.LEARNED]: { heading: "Learned", body: "The pattern was learned now. The next similar chat starts closer to this answer." },
    [RESULT.REVIEW]: { heading: "Sent for review", body: "The new pattern was sent to a reviewer. It joins the suggestions once approved." },
  }),
  refused: Object.freeze({
    heading: "Cannot teach this",
    [REFUSAL.LEARNING_OFF]: "Learning is off for this chat. Switch it on in the Learning tab to teach.",
    [REFUSAL.ALREADY_TAUGHT]: "This diagnosis was already taught or learned, so it cannot be taught again.",
    [REFUSAL.NO_DIAGNOSIS]: "There is no diagnosis in this chat to teach.",
    // The server's own refusal reasons (the Teach card shows the same ones).
    learning_disabled: "Learning is off for this session.",
    not_in_diagnosis: NO_DIAGNOSIS,
    already_decided: "Arta already learned from this session, so this pattern cannot be saved here.",
    not_your_session: "Only the engineer who ran this session can save its pattern.",
    review_unavailable: "The review queue is not reachable right now. Try again later.",
    not_recorded: NOT_RECORDED,
    fallback: "This could not be taught. Please try again.",
  }),
  // Eligibility messages (shown once, at the top of the preview; checked on open, on Preview and right before Save).
  eligibility: Object.freeze({
    not_in_diagnosis: NO_DIAGNOSIS,
    already_decided: "Arta already learned from this session, so this pattern cannot be saved here.",
    learning_disabled: "Learning is off for this session.",
    not_your_session: "Only the engineer who ran this session can save its pattern.",
    review_unavailable: "The review queue is not reachable right now. Try again later.",
    recording: RECORDING,
    recording_slow: RECORDING_SLOW,
    not_recorded: NOT_RECORDED,
    fallback: "Saving is not available right now.",
  }),
});
